import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import InputField from '../../components/InputField';
import MonthYearPicker from '../../components/MonthYearPicker';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import analytics from '../../lib/analytics';
import { fullDateFormat, shortMonth } from '../../lib/dateFormatter';
import { track } from '../../lib/heapHelpers';
import { isValidHttpUrl } from '../../lib/helpers';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const academicCalendarOptions = [
  {
    label: 'Semester',
    value: 'Semester',
  },
  {
    label: 'Quarter',
    value: 'Quarter',
  },
];

const ProgrammeLengthPage = ({
  programmeLengthStartDate,
  programmeLengthEndDate,
  academicCalendar,
  programmeLengthSupportingUrl,
  dispatchAddPageData,
  dispatchWizardNav,
}) => {
  const now = useMemo(() => {
    return new Date();
  }, []);

  const selectedCalendar =
    academicCalendarOptions.find(calendar => calendar.value === academicCalendar).value ||
    'Semester';

  const [startDateError, setStartDateError] = useState({
    shouldShowError: false,
    errorMessage: '',
  });
  const [endDateError, setEndDateError] = useState({
    shouldShowError: false,
    errorMessage: '',
  });
  const [urlError, setUrlError] = useState({
    shouldShowError: false,
    errorMessage: '',
  });

  const initialStartDate =
    programmeLengthStartDate.year !== '' && programmeLengthStartDate.month !== ''
      ? new Date(programmeLengthStartDate.year, programmeLengthStartDate.month)
      : now;

  const initialEndDate =
    programmeLengthEndDate.year !== '' && programmeLengthEndDate.month !== ''
      ? new Date(programmeLengthEndDate.year, programmeLengthEndDate.month)
      : now;

  const [startDate, setStartDate] = useState(programmeLengthStartDate);
  const [endDate, setEndDate] = useState(programmeLengthEndDate);

  const [chosenAcademicCalendar, setChosenAcademicCalendar] = useState(selectedCalendar);
  const [supportingUrl, setSupportingUrl] = useState(programmeLengthSupportingUrl);

  const changeAcademicCalendar = e => {
    setChosenAcademicCalendar(e.target.value);
  };

  const changeUrl = event => {
    setSupportingUrl(event.target.value);
    validateUrl(event.target.value);
  };

  const isStartDateFilledIn = startDate.month !== '' && startDate.year !== '';
  const isEndDateFilledIn = endDate.month !== '' && endDate.year !== '';
  const isUrlFilledIn = supportingUrl !== '';
  const isAacademicCalendarFilledIn = chosenAcademicCalendar.value !== '';

  const urlErrorTimeout = useRef();
  const validateUrl = urlToValidate => {
    if (urlErrorTimeout.current) clearTimeout(urlErrorTimeout.current);
    if (!urlToValidate.length) {
      setUrlError({
        shouldShowError: false,
        errorMessage: '',
      });
    } else if (!isValidHttpUrl(urlToValidate)) {
      urlErrorTimeout.current = setTimeout(() => {
        setUrlError({
          shouldShowError: true,
          errorMessage: 'Please enter a valid URL starting with http:// or https://',
        });
      }, 500);
    } else {
      setUrlError({
        shouldShowError: false,
        errorMessage: '',
      });
    }
  };

  const timeoutStart = useRef();
  const timeoutEnd = useRef();

  useEffect(() => {
    const validateStartDate = () => {
      const year = now.getFullYear();
      const month = now.getMonth();
      const day = now.getDate();
      const next9Months = new Date(year, month + 9, day);
      const startDateInputs = new Date(startDate.year, startDate.month);
      const endDateInputs = new Date(endDate.year, endDate.month + 1, 0);

      if (isStartDateFilledIn && next9Months < startDateInputs) {
        setStartDateError({
          shouldShowError: true,
          errorMessage: 'The course must start in the next 9 months.',
        });
      } else if (isStartDateFilledIn && isEndDateFilledIn && endDateInputs < startDateInputs) {
        setStartDateError({
          shouldShowError: true,
          errorMessage: 'The course must start before the end date.',
        });
      } else {
        setStartDateError({
          shouldShowError: false,
          errorMessage: '',
        });
      }
    };

    const validateEndDate = () => {
      const currentDate = now;
      const startDateInputs = new Date(startDate.year, startDate.month);
      const endDateInputs = new Date(endDate.year, endDate.month + 1, 0);
      if (isStartDateFilledIn && isEndDateFilledIn && endDateInputs < startDateInputs) {
        setEndDateError({
          shouldShowError: true,
          errorMessage: 'The course must end after the start date.',
        });
      } else if (isEndDateFilledIn && endDateInputs < currentDate) {
        setEndDateError({
          shouldShowError: true,
          errorMessage: 'The end date must be in the future.',
        });
      } else {
        setEndDateError({
          shouldShowError: false,
          errorMessage: '',
        });
      }
    };

    if (timeoutStart.current) clearTimeout(timeoutStart.current);
    if (timeoutEnd.current) clearTimeout(timeoutEnd.current);

    timeoutStart.current = setTimeout(validateStartDate, 500);
    timeoutEnd.current = setTimeout(validateEndDate, 500);
  }, [startDate, endDate, isStartDateFilledIn, isEndDateFilledIn, now]);

  const allFieldsValid =
    !startDateError.errorMessage &&
    !endDateError.errorMessage &&
    isAacademicCalendarFilledIn &&
    !urlError.errorMessage;

  const handleSubmit = event => {
    event.preventDefault();

    if (!allFieldsValid) return;

    analytics.sendEvent('Click - Next', 'programme-length');

    const name = `${monthNames[startDate.month]} ${startDate.year} Intake`;

    const programmeLengthStartDatePayload = payloadFactory.createAddPageDataPayload(
      'programmeLengthStartDate',
      {
        month: startDate.month.toString(),
        year: startDate.year,
      },
      'programmeLengthStartDate',
      fullDateFormat(new Date(startDate.year, startDate.month)),
    );
    const programmeLengthEndDatePayload = payloadFactory.createAddPageDataPayload(
      'programmeLengthEndDate',
      {
        month: endDate.month.toString(),
        year: endDate.year,
      },
      'programmeLengthEndDate',
      fullDateFormat(new Date(endDate.year, Number(endDate.month) + 1, 0)),
    );

    const academicCalendarPayload = payloadFactory.createAddPageDataPayload(
      'academicCalendar',
      chosenAcademicCalendar.value,
    );
    const urlPayload = payloadFactory.createAddPageDataPayload(
      'programmeLengthSupportingUrl',
      supportingUrl,
    );

    const namePayload = payloadFactory.createAddPageDataPayload('programmeName', name);
    dispatchAddPageData(programmeLengthStartDatePayload);
    dispatchAddPageData(programmeLengthEndDatePayload);
    dispatchAddPageData(academicCalendarPayload);
    dispatchAddPageData(urlPayload);
    dispatchAddPageData(namePayload);

    track('Submit- Class Info', {
      start_date: shortMonth(new Date(startDate.year, startDate.month)),
      end_date: shortMonth(new Date(endDate.year, endDate.month)),
      academic_calendar: chosenAcademicCalendar.value,
      page_url: supportingUrl,
    });

    dispatchWizardNav({ dir: 'fwd' });
  };

  const renderError = (error, bottomMargin = false) => {
    if (error && error.shouldShowError) {
      return (
        <div
          className={`text-error desktop-d6 ${bottomMargin ? 'margin-bottom' : ''}`}
          data-testid="error-message"
        >
          {error.errorMessage}
        </div>
      );
    }

    return null;
  };

  const handleStartDateChange = date => {
    setStartDate({ year: date.year, month: date.month - 1 });
  };

  const handleEndDateChange = date => {
    setEndDate({ year: date.year, month: date.month - 1 });
  };

  const helpText =
    'We are only able to issue loans for a programme that starts in 12 months or less from now.';

  return (
    <PageLayout title="What is the full duration of your programme?" helpText={helpText}>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="px-2 my-4 w-full">
            <div>
              <MonthYearPicker
                label="Starting date"
                initialDate={initialStartDate}
                minDate={now}
                onChange={handleStartDateChange}
                inputProps="start-date"
              />
              {renderError(startDateError)}
            </div>
          </div>
        </div>
        <div className="row !mt-0">
          <div className="px-2 my-4 w-full">
            <div>
              <MonthYearPicker
                label="End date"
                initialDate={initialEndDate}
                minDate={now}
                onChange={handleEndDateChange}
                inputProps="end-date"
              />
              {renderError(endDateError)}
            </div>
          </div>
        </div>
        <div className="row !mt-0">
          <div className="px-2 my-4 w-full">
            <label className="form-label" htmlFor="academic-calendar">
              Academic calendar year
            </label>

            <Select
              id="academic-calendar"
              value={chosenAcademicCalendar}
              onChange={changeAcademicCalendar}
              options={academicCalendarOptions}
            />
          </div>
        </div>
        <p className="mt-4">
          We will need to validate this information, so please tell us the URL of the page where you
          found it.
        </p>
        <div className="row !mt-0">
          <div className="px-2 my-4 w-full">
            <div>
              <InputField
                type="string"
                id="supporting-url"
                data-testid="supporting-url-input"
                placeholder="Page URL"
                value={supportingUrl}
                onChange={changeUrl}
                error={isUrlFilledIn && urlError.shouldShowError}
                label="Page URL"
              />
            </div>
            {isUrlFilledIn && renderError(urlError, true)}
          </div>
        </div>
        <div className="mt-12">
          <Button
            type="submit"
            className="niso-button"
            onClick={e => handleSubmit(e)}
            isDisabled={!allFieldsValid}
          >
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

ProgrammeLengthPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  programmeLengthStartDate: PropTypes.shape({
    month: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }).isRequired,
  programmeLengthEndDate: PropTypes.shape({
    month: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }).isRequired,
  academicCalendar: PropTypes.string.isRequired,
  programmeLengthSupportingUrl: PropTypes.string,
};

ProgrammeLengthPage.defaultProps = {
  programmeLengthStartDate: { month: '', year: '' },
  programmeLengthEndDate: { month: '', year: '' },
  academicCalendar: 'Semester',
  programmeLengthSupportingUrl: '',
};

export const mapStateToProps = state => ({
  programmeLengthStartDate: state.builder.ui.programmeLengthStartDate,
  programmeLengthEndDate: state.builder.ui.programmeLengthEndDate,
  academicCalendar: state.builder.ui.academicCalendar,
  programmeLengthSupportingUrl: state.builder.ui.programmeLengthSupportingUrl,
  course: state.builder.ui.course,
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { ProgrammeLengthPage };

export default connect(mapStateToProps, mapDispatchToProps)(ProgrammeLengthPage);
